import { useCallback, useEffect, useState } from 'react';

import type { PricingOptionIndex } from '../../types';

import type { OverrideBasis, OverrideSet, OverrideTier, OverridesValues } from './types';

const DEFAULT_SET: OverrideSet = {
  basis: 'vendor_revenue',
  tiers: [{ to: 0, comms: 0 }],
};

const DEFAULT_VALUES: OverridesValues = {
  options: { 0: DEFAULT_SET, 1: DEFAULT_SET, 2: DEFAULT_SET },
};

// activeOption comes from Package Options so all option toggles stay in sync.
export function useOverrides(offerId: string | undefined, activeOption: PricingOptionIndex) {
  const [overrides, setOverrides] = useState<OverridesValues>(DEFAULT_VALUES);

  useEffect(() => {
    setOverrides(DEFAULT_VALUES);
  }, [offerId]);

  const updateActiveSet = useCallback(
    (update: (set: OverrideSet) => OverrideSet) => {
      setOverrides((current) => ({
        options: { ...current.options, [activeOption]: update(current.options[activeOption]) },
      }));
    },
    [activeOption],
  );

  const setBasis = useCallback(
    (basis: OverrideBasis) => updateActiveSet((set) => ({ ...set, basis })),
    [updateActiveSet],
  );

  const addTier = useCallback(
    () => updateActiveSet((set) => ({ ...set, tiers: [...set.tiers, { to: 0, comms: 0 }] })),
    [updateActiveSet],
  );

  const updateTier = useCallback(
    (index: number, changes: Partial<OverrideTier>) =>
      updateActiveSet((set) => ({
        ...set,
        tiers: set.tiers.map((tier, tierIndex) => (tierIndex === index ? { ...tier, ...changes } : tier)),
      })),
    [updateActiveSet],
  );

  const removeTier = useCallback(
    (index: number) =>
      updateActiveSet((set) => ({ ...set, tiers: set.tiers.filter((_, tierIndex) => tierIndex !== index) })),
    [updateActiveSet],
  );

  const activeOverrides = overrides.options[activeOption];

  return { overrides, activeOverrides, setBasis, addTier, updateTier, removeTier, setOverrides };
}
